<template>

  <div class="card-base card-shadow--medium widget small-widget queue-widget">
    <div class="widget-value ph-10">
      <div class="flex mb-10 mt-10 align-center">
        <span class="mr-5 flex justify-center align-center">
          <v-svg width="18" height="18" class="voxo-red" sprite="call-inbound"/>
        </span>
        <el-input
          class="widget-name-input ml-10"
          v-model="widgetName"
        />
        <el-button
          @click="removeWidget"
          class="table-action-button ml-10"
        >
          <i class="el-icon-delete voxo-dark-red" />
        </el-button>
      </div>

      <h1 class="m-0 text-truncate voxo-blue text-center stat-number">
        {{ widgetValue }}
      </h1>
      <div class="flex justify-space-between mb-10">
        <div class="stat-breakdown">
          <div class="text-left">
            <span> {{ abandonRateValue }} %</span>
          </div>
          <h5 class="mt-0 mb-0"> RATE </h5>
        </div>
        <div class="stat-breakdown">
          <div class="text-right">
            <span> {{ avgAbandonWaitValue }} s</span>
          </div>
          <h5 class="mt-0 mb-0"> AVG WAIT </h5>
        </div>
      </div>
    </div>

    <div class="stat-widget-table ph-10" v-show="isMultiQueue">
      <vxe-table
        round
        ref="xGrid"
        height="100%"
        border="inner"
        class="user-stats-table"
        empty-text="No Data"
        auto-resize
        sync-resize
        :show-overflow="true"
        :checkbox-config="{checkAll: determineCheckAll()}"
        @checkbox-change="checkboxesChanged"
        @checkbox-all="checkboxesChanged"
        :scroll-y="{enabled: true, gt: 10, oSize: 0}"
        :data="queueStoreData"
      >
        <vxe-table-column
          field="checkbox"
          type="checkbox"
          width="30"
        />
        <vxe-table-column
          field="queueId"
          title="Queue"
          min-width="200"
          sortable
          :formatter="getQueueName"
        />
        <vxe-table-column
          field="abandoned"
          title="Count"
          min-width="80"
          sortable
          sort-type="number"
        />
      </vxe-table>
    </div>

  </div>

</template>

<script>

import {mapGetters} from "vuex";
import {debounce} from "lodash";

export default {
  name: 'AbandonedCallsQueue',
  props: {
    widgetIndex: {required: true},
    title: {required: true},
    filterQueueIds: {required: true},
    queueNameKey: {required: true},
  },
  data() {
    return {
      widgetValue: 0,
      abandonRateValue: 0,
      avgAbandonWaitValue: 0,
      widgetName: this.title,
      filtersActive: false,
      filterQueues: this.filterQueueIds,
      viewStyle: 'default'
    }
  },
  computed: {

    ...mapGetters('queueDashQueues', {queueStoreData: 'list'}),

    totalAbandoned() {
      return this.$store.state.queueManagerAggregates.totalAbandoned;
    },

    abandonRate() {
      return this.$store.state.queueManagerAggregates.abandonRate;
    },

    avgAbandonWaitTime(){
      return this.$store.state.queueManagerAggregates.avgAbandonWaitTime;
    },

    isMultiQueue() {
      return this.queueStoreData.length > 1;
    }

  },
  watch: {
    totalAbandoned: debounce(function () {
      this.widgetValue = this.filtersActive ? this.getFilteredValues() : this.totalAbandoned;
    }, 150),
    abandonRate: debounce(function () {
      this.abandonRateValue = this.filtersActive ? this.getFilteredValues() : this.abandonRate;
    }, 150),
    avgAbandonWaitTime: debounce(function () {
      this.avgAbandonWaitValue = this.filtersActive ? this.getFilteredValues() : this.avgAbandonWaitTime;
    }, 150),
    widgetName: debounce(function() {
      if(this.widgetName === this.title) return;
      this.$emit('nameUpdate', {name: this.widgetName, index: this.widgetIndex});
    }, 750),
    filterQueues: debounce(function () {
      this.$emit('filterQueuesUpdate', {filterQueueIds: this.filterQueues, index: this.widgetIndex});
    }, 750)
  },
  methods: {

    getQueueName(data) {
      const { cellValue } = data;
      return this.queueNameKey[cellValue] ? this.queueNameKey[cellValue] : 'Unknown';
    },

    removeWidget(){
      this.$emit('removeWidget', this.widgetIndex);
    },

    checkboxesChanged({records}) {
      this.filterQueues = records.map(item => item.queueId);

      if(this.filterQueues.length === this.queueStoreData.length) {
        this.widgetValue = this.totalAbandoned;
        this.abandonRateValue = this.abandonRate;
        this.avgAbandonWaitValue = this.avgAbandonWaitTime
        this.filtersActive = false;
        return;
      }

      this.filtersActive = true;
      this.getFilteredValues();
    },

    getFilteredValues() {

      //no need to compute if we are getting the full aggregate
      if(this.filterQueues.length === this.queueStoreData.length) {
        this.widgetValue = this.totalAbandoned;
        this.abandonRateValue = this.abandonRate;
        this.avgAbandonWaitValue = this.avgAbandonWaitTime
        this.filtersActive = false;
        return;
      }

      //if no queues are checked set all zero
      if(!this.filterQueues.length) {
        this.widgetValue = 0;
        this.abandonRateValue = 0;
        this.avgAbandonWaitValue = 0;
        return;
      }

      //if filter queues are active, do the math
      let totalAbandoned = 0;
      let totalInbound = 0;
      let totalAbandonWaitTime = 0;
      for(let item of this.queueStoreData){
        if(this.filterQueues.includes(item.queueId)){
          totalAbandoned += item.abandoned;
          totalInbound += item.totalInbound;
          totalAbandonWaitTime += item.waitTimeAbandoned;
        }
      }

      this.widgetValue = totalAbandoned;
      this.abandonRateValue = totalInbound ? +(totalAbandoned / totalInbound * 100).toFixed(0) : 0;
      this.avgAbandonWaitValue = totalAbandoned ? +(totalAbandonWaitTime / totalAbandoned).toFixed(0): 0;

    },

    determineCheckAll() {

      const hasFilter = this.filterQueues.length !== this.queueStoreData.length;

      if(!hasFilter){
        this.getInitialValue();
        return true;
      }

      if(hasFilter && this.isMultiQueue){
        this.filtersActive = true;
        this.updateCheckboxes();
        return false;
      }

    },

    async updateCheckboxes() {
      setTimeout(() => {
        const checkedRows = this.queueStoreData.filter(item => this.filterQueues.includes(item.queueId));
        const grid = this.$refs.xGrid;
        if(grid){
          grid.setCheckboxRow(checkedRows,checkedRows);
          this.getFilteredValues();
        }
      }, 500)
    },

    getInitialValue() {
      setTimeout( () => {
        this.getFilteredValues()
      }, 500)
    }
  }
}

</script>
